import { useState } from 'react'
import { useAccessibility } from '../contexts/AccessibilityContext'
import { SeniorButton } from './SeniorButton'

export const AccessibilityPanel = () => {
  const { settings, updateFontSize, updateContrast, toggleAnimations } = useAccessibility()
  const [isOpen, setIsOpen] = useState(false)

  const fontSizeOptions = [
    { value: 'small' as const, label: 'Мелкий', description: 'Для хорошего зрения' },
    { value: 'medium' as const, label: 'Обычный', description: 'Стандартный размер' },
    { value: 'large' as const, label: 'Крупный', description: 'Удобно читать' },
    { value: 'extra-large' as const, label: 'Очень крупный', description: 'Максимальный комфорт' }
  ]

  if (!isOpen) {
    return (
      <div className="fixed top-4 right-4 z-50">
        <SeniorButton
          onClick={() => setIsOpen(true)}
          variant="secondary"
          size="medium"
          icon="⚙️"
          aria-label="Открыть настройки доступности"
        >
          Настройки
        </SeniorButton>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">
            Настройки удобства
          </h2>
          <SeniorButton
            onClick={() => setIsOpen(false)}
            variant="secondary"
            size="medium"
            aria-label="Закрыть настройки"
          >
            ✕
          </SeniorButton>
        </div>

        <div className="space-y-6">
          {/* Font Size Settings */}
          <div>
            <h3 className="text-xl font-semibold text-gray-900 mb-4">
              Размер текста
            </h3>
            <div className="grid grid-cols-1 gap-3">
              {fontSizeOptions.map((option) => (
                <button
                  key={option.value}
                  onClick={() => updateFontSize(option.value)}
                  className={`
                    p-4 rounded-lg border-2 text-left transition-colors
                    ${settings.fontSize === option.value
                      ? 'border-blue-500 bg-blue-50 text-blue-900'
                      : 'border-gray-300 hover:border-gray-400 text-gray-700'
                    }
                  `}
                  aria-pressed={settings.fontSize === option.value}
                >
                  <div className="font-semibold text-lg">{option.label}</div>
                  <div className="text-sm text-gray-600">{option.description}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Contrast Settings */}
          <div>
            <h3 className="text-xl font-semibold text-gray-900 mb-4">
              Контрастность
            </h3>
            <div className="grid grid-cols-1 gap-3">
              <button
                onClick={() => updateContrast('normal')}
                className={`
                  p-4 rounded-lg border-2 text-left transition-colors
                  ${settings.contrast === 'normal'
                    ? 'border-blue-500 bg-blue-50 text-blue-900'
                    : 'border-gray-300 hover:border-gray-400 text-gray-700'
                  }
                `}
                aria-pressed={settings.contrast === 'normal'}
              >
                <div className="font-semibold text-lg">Обычная</div>
                <div className="text-sm text-gray-600">Стандартные цвета</div>
              </button>
              <button
                onClick={() => updateContrast('high')}
                className={`
                  p-4 rounded-lg border-2 text-left transition-colors
                  ${settings.contrast === 'high'
                    ? 'border-blue-500 bg-blue-50 text-blue-900'
                    : 'border-gray-300 hover:border-gray-400 text-gray-700'
                  }
                `}
                aria-pressed={settings.contrast === 'high'}
              >
                <div className="font-semibold text-lg">Высокая</div>
                <div className="text-sm text-gray-600">Четкие черно-белые цвета</div>
              </button>
            </div>
          </div>

          {/* Animation Settings */}
          <div>
            <h3 className="text-xl font-semibold text-gray-900 mb-4">
              Анимация
            </h3>
            <button
              onClick={toggleAnimations}
              className={`
                w-full p-4 rounded-lg border-2 text-left transition-colors
                ${settings.animationsEnabled
                  ? 'border-green-500 bg-green-50 text-green-900'
                  : 'border-gray-300 bg-gray-50 text-gray-700'
                }
              `}
              aria-pressed={settings.animationsEnabled}
            >
              <div className="font-semibold text-lg">
                {settings.animationsEnabled ? '✅ Включена' : '❌ Отключена'}
              </div>
              <div className="text-sm text-gray-600">
                {settings.animationsEnabled 
                  ? 'Плавные переходы и движения'
                  : 'Без движений для комфорта'
                }
              </div>
            </button>
          </div>

          {/* Keyboard Shortcuts */}
          <div>
            <h3 className="text-xl font-semibold text-gray-900 mb-4">
              Быстрые клавиши
            </h3>
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="font-medium">Alt + S</span>
                  <span>Открыть настройки</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">Alt + M</span>
                  <span>Перейти к содержанию</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">Alt + 1</span>
                  <span>Тренировка</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">Alt + 2</span>
                  <span>Обучение</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">Escape</span>
                  <span>Закрыть окна</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">Tab</span>
                  <span>Навигация по элементам</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-8 pt-6 border-t border-gray-200">
          <SeniorButton
            onClick={() => setIsOpen(false)}
            variant="primary"
            size="large"
            className="w-full"
          >
            Сохранить настройки
          </SeniorButton>
        </div>
      </div>
    </div>
  )
}